import React from 'react';
import { TimelineArticle } from '../types/timeline';
import { SupportedLanguage } from '../types/language';
import { useMultilingual } from '../hooks/useMultilingual';
import { format, parseISO } from 'date-fns';
import { 
  NewspaperIcon,
  ArrowTopRightOnSquareIcon,
  FireIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Modal } from './common/Modal';
import { useTranslation } from 'react-i18next';

interface TimelineArticlesProps {
  date: string;
  articles: TimelineArticle[];
  lingeringThreats?: TimelineArticle[];
  onClose: () => void;
  onArticleSelect: (article: TimelineArticle) => void;
  isMilitaryMode?: boolean;
  language?: SupportedLanguage;
}

export const TimelineArticles: React.FC<TimelineArticlesProps> = ({
  date,
  articles,
  lingeringThreats = [],
  onClose,
  onArticleSelect,
  isMilitaryMode = false,
  language = 'en',
}) => {
  const parseDateValue = (value: string): Date => {
    return value.includes('T') ? new Date(value) : parseISO(value);
  };
  const formattedDate = format(parseDateValue(date), 'EEEE, MMMM d, yyyy');
  const { getMultilingualContent, getMultilingualArray } = useMultilingual(language);
  const { t } = useTranslation();

  const getRiskColor = (score: number) => {
    if (score >= 8) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 6) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (score >= 4) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 8) return t('public.riskLevels.critical').toUpperCase();
    if (score >= 6) return t('public.riskLevels.high').toUpperCase();
    if (score >= 4) return t('public.riskLevels.medium').toUpperCase();
    return t('public.riskLevels.low').toUpperCase();
  };

  const categoryColors: Record<string, string> = {
    MILITARY: 'bg-red-100 text-red-800',
    CYBER: 'bg-purple-100 text-purple-800',
    ECONOMIC: 'bg-blue-100 text-blue-800',
    DIPLOMATIC: 'bg-green-100 text-green-800',
    INFORMATION_WARFARE: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <Modal isOpen={true} onClose={onClose} panelClassName="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1">{t('public.timelineArticles.title', { date: formattedDate })}</h2>
              <p className="text-blue-100">{t('public.timelineArticles.countAnalyzed', { count: articles.length })}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Articles list */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
          {articles.length === 0 && lingeringThreats.length === 0 ? (
            <div className="text-center py-12">
              <NewspaperIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{t('public.timelineArticles.noneOnDate')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Today's articles */}
              {articles.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <NewspaperIcon className="w-5 h-5 mr-2" />
                    {t('public.timelineArticles.newFromThisDay')}
                  </h3>
                  {isMilitaryMode && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="text-sm text-red-700">
                        {t('public.timelineArticles.militaryModeNotice')}
                      </div>
                    </div>
                  )}
                  <div className="space-y-4">
              {[...articles].sort((a, b) => b.overallRiskScore - a.overallRiskScore).map((article) => (
                <div
                  key={article.id}
                  className="border rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => onArticleSelect(article)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span>{article.source}</span>
                        <span>•</span>
                        <span>{format(parseDateValue(article.publishedAt), 'h:mm a')}</span>
                        {article.isHighImpact && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-orange-600">
                              <FireIcon className="w-4 h-4" />
                              High Impact
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`ml-4 px-3 py-1 rounded-lg border ${getRiskColor(article.overallRiskScore)}`}>
                      <div className="text-2xl font-bold">{article.overallRiskScore.toFixed(1)}</div>
                      <div className="text-xs font-medium">{getRiskLabel(article.overallRiskScore)}</div>
                    </div>
                  </div>

                  {/* Categories with scores for transparency */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {article.categoryScores ? (
                      // Show category scores when available
                      Object.entries(article.categoryScores)
                        .filter(([_, score]) => score > 0)
                        .sort(([_a, scoreA], [_b, scoreB]) => scoreB - scoreA)
                        .map(([category, score]) => (
                          <span
                            key={category}
                            className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                              categoryColors[category] || 'bg-gray-100 text-gray-800'
                            } ${
                              category === 'MILITARY' ? 'ring-2 ring-red-300' : ''
                            }`}
                          >
                            {category}: {score.toFixed(1)}
                          </span>
                        ))
                    ) : (
                      // Fallback to original categories
                      article.categories.map((category) => (
                        <span
                          key={category}
                          className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                            categoryColors[category] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {category}
                        </span>
                      ))
                    )}
                    {article.impactPercentage && article.impactPercentage > 20 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                        <SparklesIcon className="w-3 h-3" />
                        {article.impactPercentage.toFixed(0)}% of day's risk
                      </span>
                    )}
                  </div>

                  {/* Key Points */}
                  {getMultilingualArray(article.keyPoints).length > 0 && (
                    <div className="mb-3">
                      <ul className="space-y-1">
                        {getMultilingualArray(article.keyPoints).slice(0, 3).map((point, index) => (
                          <li key={index} className="text-sm text-gray-600 flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span className="line-clamp-1">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Summary */}
                  {getMultilingualContent(article.summary) && (
                    <p className="text-gray-900 mb-3">
                      {getMultilingualContent(article.summary)}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                      {t('public.articleDetail.readFullArticle')}
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onArticleSelect(article);
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      {t('public.timelineArticles.viewDetails')}
                    </button>
                  </div>
                </div>
              ))}
                  </div>
                </div>
              )}

              {/* Lingering threats */}
              {lingeringThreats.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FireIcon className="w-5 h-5 mr-2 text-orange-500" />
                    {t('public.timelineArticles.ongoingThreatsTitle')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">{t('public.timelineArticles.ongoingThreatsSubtitle')}</p>
                  <div className="space-y-4">
                    {[...lingeringThreats].sort((a, b) => (b.decayedScore || b.overallRiskScore) - (a.decayedScore || a.overallRiskScore)).map((article) => (
                      <div
                        key={article.id}
                        className="border rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer group bg-orange-50 border-orange-200"
                        onClick={() => onArticleSelect(article)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                              {article.title}
                            </h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                              <span>{article.source}</span>
                              <span>•</span>
                              <span className="text-orange-600 font-medium">
                                {article.daysAgo} {article.daysAgo === 1 ? t('public.articleDetail.day') : t('public.articleDetail.days')} {t('public.articleDetail.ago')}
                              </span>
                              <span>•</span>
                              <span className="text-orange-600">
                                {t('public.timelineArticles.impactRemaining', { percent: ((article.decayFactor || 0) * 100).toFixed(0) })}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-center">
                              <div className="text-xl font-bold text-orange-600">
                                {(article.decayedScore || article.overallRiskScore).toFixed(1)}
                              </div>
                              <div className="text-xs text-gray-500">
                                ({t('public.articleDetail.was')} {article.overallRiskScore.toFixed(1)})
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Categories */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {article.categories.map((category) => (
                            <span
                              key={category}
                              className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                                categoryColors[category] || 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {category}
                            </span>
                          ))}
                        </div>

                        {/* Summary */}
                        {getMultilingualContent(article.summary) && (
                          <p className="text-gray-900 mb-3">
                            {getMultilingualContent(article.summary)}
                          </p>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                          <a
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                          >
                            {t('public.articleDetail.readFullArticle')}
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
    </Modal>
  );
};
